import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import SidebarDrawerContext from 'context/SidebarDrawerContext';

/**
 * Estado del panel de navegación móvil.
 *
 * Aquí vivía un mecanismo bastante más elaborado: al tocar un ítem del menú se cerraba el panel y
 * la navegación quedaba EN ESPERA hasta que la animación de salida avisara de que había terminado,
 * con un temporizador de respaldo por si ese aviso no llegaba nunca. Existía porque el panel se
 * construía dentro del layout de cada ruta, así que navegar lo destruía a media animación.
 *
 * Ese motivo ya no existe: el panel se monta una sola vez por encima de las rutas. Y el mecanismo
 * tenía tres piezas que podían fallar por separado (el aviso de fin de animación, el temporizador y
 * la acción guardada), cualquiera de las cuales dejaba el panel abierto o la navegación colgada.
 *
 * Ahora es lo más simple que puede ser: se cierra y se navega en el mismo momento. La animación de
 * salida corre sola mientras la pantalla nueva se dibuja, y el panel no depende de que nadie le
 * avise de nada.
 */
function SidebarDrawerContextProvider({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const openMobileSidebar = useCallback(() => setMobileOpen(true), []);
  const closeMobileSidebar = useCallback(() => setMobileOpen(false), []);

  /**
   * Cierra el panel y ejecuta la acción de inmediato.
   *
   * Se mantiene el nombre porque es el que usan los ítems del menú, pero ya no hay ninguna espera:
   * `action` corre en el mismo instante que el cierre.
   */
  const closeMobileSidebarThen = useCallback((action) => {
    setMobileOpen(false);
    if (typeof action === 'function') action();
  }, []);

  /**
   * Cierre garantizado en cada cambio de ruta.
   *
   * Es la red que cubre TODOS los caminos, incluidos los que no pasan por el menú: el botón atrás,
   * una redirección de un guard, un enlace dentro del propio panel o cualquier navegación futura
   * que alguien añada sin acordarse de cerrarlo.
   */
  const { pathname } = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const value = useMemo(
    () => ({
      mobileOpen,
      openMobileSidebar,
      closeMobileSidebar,
      closeMobileSidebarThen,
      // Se conserva por compatibilidad con el drawer, que lo enlaza al fin de su animación. Ya no
      // hay nada pendiente que ejecutar, así que no hace nada.
      notifyMobileSidebarClosed: () => {}
    }),
    [mobileOpen, openMobileSidebar, closeMobileSidebar, closeMobileSidebarThen]
  );

  return <SidebarDrawerContext.Provider value={value}>{children}</SidebarDrawerContext.Provider>;
}

SidebarDrawerContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default SidebarDrawerContextProvider;
